import React, { useState, useEffect } from 'react';
import { Plus, Building2, Edit, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import companyService from '../services/company.service';
import CompanyCreationForm from './CompanyCreationForm';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await companyService.getAllCompanies();
      if (response.success) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = () => {
    setEditingCompany(null);
    setShowAddForm(true);
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setShowAddForm(true);
  };

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        const response = await companyService.deleteCompany(companyId);
        if (response.success) {
          toast.success('Company deleted successfully');
          fetchCompanies();
        }
      } catch (error) {
        console.error('Error deleting company:', error);
        toast.error('Failed to delete company');
      }
    }
  };

  const handleCompanyCreated = (newCompany) => {
    if (editingCompany) {
      // Update existing company in the list
      setCompanies(prev => prev.map(company => 
        company._id === editingCompany._id ? newCompany : company
      ));
    } else {
      // Add new company to the list
      setCompanies(prev => [newCompany, ...prev]);
    }
    setShowAddForm(false);
    setEditingCompany(null);
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setEditingCompany(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Companies</h1>
          <p className="text-gray-600">Manage your companies and their information</p>
        </div>
        <button
          onClick={handleAddCompany}
          className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus size={20} />
          Add Company
        </button>
      </div>

      {/* Companies List */}
      {companies.length === 0 ? (
        <div className="text-center py-12">
          <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No companies yet</h3>
          <p className="text-gray-500 mb-4">Get started by adding your first company</p>
          <button
            onClick={handleAddCompany}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Add Your First Company
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company) => (
            <div key={company._id} className="bg-white rounded-lg shadow-md p-6 border">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Building2 size={24} className="text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{company.name}</h3>
                    {company.industry && (
                      <p className="text-sm text-gray-500">{company.industry}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEditCompany(company)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Edit company"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteCompany(company._id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete company"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {company.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {company.description}
                </p>
              )}

              <div className="space-y-2">
                {company.website && (
                  <div className="flex items-center gap-2 text-sm">
                    <ExternalLink size={14} className="text-gray-400" />
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-600 hover:text-orange-700"
                    >
                      {company.website}
                    </a>
                  </div>
                )}
                {company.country && (
                  <p className="text-sm text-gray-500">📍 {company.country}</p>
                )}
                {company.size && (
                  <p className="text-sm text-gray-500">👥 {company.size} employees</p>
                )}
              </div>

              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Created</span>
                  <span className="text-gray-900">
                    {new Date(company.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Company Creation Form Modal */}
      {showAddForm && (
        <CompanyCreationForm
          company={editingCompany}
          onCompanyCreated={handleCompanyCreated}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default Companies; 